<script lang="ts">
  import { Consultorios, Liquidaciones, Tarifas, Seguros, CuentasConsultorio, AcuerdosClinica, Compartido,
    type Consultorio, type Profesional, type ReporteIngresosConsultorio, type Liquidacion,
    type Tarifa, type TarifaConvenio, type Aseguradora, type ReporteEstadoCuenta,
    type CuentaConsultorio, type AcuerdoClinica, type EstadoCuentaCompartido } from '../lib/api';

  let tab = $state<'crear'|'lista'|'tarifas'|'finanzas'|'acuerdos'>('crear');

  let consultorios = $state<Consultorio[]>([]);
  let profesionales = $state<Profesional[]>([]);
  let error = $state<string|null>(null);
  let cargando = $state(false);

  let nuevoCons = $state<Consultorio>({ codigo:'', nombre:'', tipo:'PROPIO', vinculo:'INTERNO', modeloCobro:'COMISION', comisionHospitalPct:0, rentaMensual:0, activo:true });
  let nuevoProf = $state<Profesional>({ nombre:'', colegiado:'', nit:'', tipoRelacion:'EMPLEADO', honorarioPct:100, activo:true });

  // Reporte
  const hoy = new Date();
  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const iso = (d: Date) => d.toISOString().slice(0,10);
  let desde = $state(iso(primero));
  let hasta = $state(iso(hoy));
  let reporte = $state<ReporteIngresosConsultorio|null>(null);

  async function recargar() {
    cargando = true; error = null;
    try {
      consultorios = await Consultorios.list(false);
      profesionales = await Consultorios.profesionales(false);
    } catch (e: any) { error = e.message; }
    cargando = false;
  }

  async function crearConsultorio() {
    error = null;
    if (!nuevoCons.codigo || !nuevoCons.nombre) { error = 'Código y nombre del consultorio son requeridos'; return; }
    try {
      const body: Consultorio = { ...nuevoCons };
      if (body.tipo === 'PROPIO') { body.modeloCobro = null; body.comisionHospitalPct = 0; body.rentaMensual = 0; }
      if (body.vinculo !== 'EXTERNO') { body.tenantExternoId = null; }
      await Consultorios.crear(body);
      nuevoCons = { codigo:'', nombre:'', tipo:'PROPIO', vinculo:'INTERNO', modeloCobro:'COMISION', comisionHospitalPct:0, rentaMensual:0, activo:true };
      await recargar();
    } catch (e: any) { error = e.message; }
  }

  async function toggleConsultorio(c: Consultorio) {
    try { await Consultorios.actualizar(c.id!, { ...c, activo: !c.activo }); await recargar(); }
    catch (e: any) { error = e.message; }
  }

  async function crearProfesional() {
    error = null;
    if (!nuevoProf.nombre) { error = 'Nombre del profesional requerido'; return; }
    try {
      await Consultorios.crearProf({ ...nuevoProf });
      nuevoProf = { nombre:'', colegiado:'', nit:'', tipoRelacion:'EMPLEADO', honorarioPct:100, activo:true };
      await recargar();
    } catch (e: any) { error = e.message; }
  }

  async function toggleProfesional(p: Profesional) {
    try { await Consultorios.actualizarProf(p.id!, { ...p, activo: !p.activo }); await recargar(); }
    catch (e: any) { error = e.message; }
  }

  async function generarReporte() {
    error = null; reporte = null;
    try { reporte = await Consultorios.reporteIngresos(desde, hasta); }
    catch (e: any) { error = e.message; }
  }

  function centros(m: Record<string, number>): string {
    return Object.entries(m).map(([k,v]) => `${k}: Q${v}`).join(' · ');
  }

  // Liquidaciones
  let liquidaciones = $state<Liquidacion[]>([]);
  let liqConsultorioId = $state('');
  let info = $state<string|null>(null);

  async function cargarLiquidaciones() {
    try { liquidaciones = await Liquidaciones.list(); } catch (e: any) { error = e.message; }
  }
  async function generarLiquidaciones() {
    error = null; info = null;
    try {
      const r = await Liquidaciones.generar(desde, hasta, liqConsultorioId || undefined);
      info = r.length ? `${r.length} liquidación(es) generada(s).` : 'Sin consultorios arrendados con ingresos en el período.';
      await cargarLiquidaciones();
    } catch (e: any) { error = e.message; }
  }
  async function pagarLiq(l: Liquidacion) {
    try { await Liquidaciones.pagar(l.id!); await cargarLiquidaciones(); } catch (e: any) { error = e.message; }
  }
  async function emitirLiq(l: Liquidacion) {
    try { await Liquidaciones.emitir(l.id!); await cargarLiquidaciones(); } catch (e: any) { error = e.message; }
  }

  // Tarifario interno
  let tarifas = $state<Tarifa[]>([]);
  let nuevaTarifa = $state<Tarifa>({ codigo:'', descripcion:'', centroIngreso:'', precioPublico:0, precioInterno:null, activo:true });

  async function cargarTarifas() {
    try { tarifas = await Tarifas.list(false); } catch (e: any) { error = e.message; }
  }
  async function crearTarifa() {
    error = null;
    if (!nuevaTarifa.codigo || !nuevaTarifa.descripcion) { error = 'Código y descripción de la tarifa son requeridos'; return; }
    try {
      await Tarifas.crear({ ...nuevaTarifa, centroIngreso: nuevaTarifa.centroIngreso || null });
      nuevaTarifa = { codigo:'', descripcion:'', centroIngreso:'', precioPublico:0, precioInterno:null, activo:true };
      await cargarTarifas();
    } catch (e: any) { error = e.message; }
  }
  async function toggleTarifa(t: Tarifa) {
    try { await Tarifas.actualizar(t.id!, { ...t, activo: !t.activo }); await cargarTarifas(); }
    catch (e: any) { error = e.message; }
  }

  // Tarifas por convenio
  let aseguradoras = $state<Aseguradora[]>([]);
  let convenios = $state<TarifaConvenio[]>([]);
  let nuevoConvenio = $state<TarifaConvenio>({ codigo:'', aseguradoraId:'', precioConvenio:0, activo:true });

  async function cargarConvenios() {
    try {
      aseguradoras = await Seguros.list(false);
      convenios = await Tarifas.convenios();
    } catch (e: any) { error = e.message; }
  }
  async function crearConvenio() {
    error = null;
    if (!nuevoConvenio.codigo || !nuevoConvenio.aseguradoraId) { error = 'Código y aseguradora del convenio son requeridos'; return; }
    try {
      await Tarifas.crearConvenio({ ...nuevoConvenio });
      nuevoConvenio = { codigo:'', aseguradoraId:'', precioConvenio:0, activo:true };
      await cargarConvenios();
    } catch (e: any) { error = e.message; }
  }
  async function toggleConvenio(c: TarifaConvenio) {
    try { await Tarifas.actualizarConvenio(c.id!, { ...c, activo: !c.activo }); await cargarConvenios(); }
    catch (e: any) { error = e.message; }
  }
  function nombreAseguradora(id: string): string {
    return aseguradoras.find(a => a.id === id)?.nombre ?? id;
  }

  // Estado de cuenta de consultorios
  let estadoCuenta = $state<ReporteEstadoCuenta|null>(null);
  async function generarEstadoCuenta() {
    error = null; estadoCuenta = null;
    try { estadoCuenta = await Consultorios.estadoCuenta(desde, hasta); }
    catch (e: any) { error = e.message; }
  }

  // Cuentas por cobrar a consultorios
  let cuentasCons = $state<CuentaConsultorio[]>([]);
  async function cargarCuentasCons() {
    try { cuentasCons = await CuentasConsultorio.list(); } catch (e: any) { error = e.message; }
  }
  async function abonar(c: CuentaConsultorio) {
    error = null;
    const txt = prompt(`Abono para ${c.consultorioNombre ?? 'consultorio'} (saldo Q${c.saldo}):`, String(c.saldo));
    if (txt == null) return;
    const monto = Number(txt);
    if (!Number.isFinite(monto) || monto <= 0) { error = 'Monto de abono inválido'; return; }
    try { await CuentasConsultorio.abonar(c.consultorioId, { monto }); await cargarCuentasCons(); }
    catch (e: any) { error = e.message; }
  }

  // Modelo B · acuerdos con clínicas externas
  let acuerdos = $state<AcuerdoClinica[]>([]);
  let entrantes = $state<AcuerdoClinica[]>([]);
  let acuerdoConsultorioId = $state('');
  async function cargarAcuerdos() {
    try { acuerdos = await AcuerdosClinica.list(); entrantes = await AcuerdosClinica.entrantes(); }
    catch (e: any) { error = e.message; }
  }
  async function proponerAcuerdo() {
    error = null;
    if (!acuerdoConsultorioId) { error = 'Selecciona un consultorio externo'; return; }
    try { await AcuerdosClinica.proponer({ consultorioId: acuerdoConsultorioId }); acuerdoConsultorioId = ''; await cargarAcuerdos(); }
    catch (e: any) { error = e.message; }
  }
  async function suspenderAcuerdo(a: AcuerdoClinica) {
    try { await AcuerdosClinica.suspender(a.id); await cargarAcuerdos(); } catch (e: any) { error = e.message; }
  }
  async function terminarAcuerdo(a: AcuerdoClinica) {
    try { await AcuerdosClinica.terminar(a.id); await cargarAcuerdos(); } catch (e: any) { error = e.message; }
  }
  async function aceptarEntrante(a: AcuerdoClinica) {
    try { await AcuerdosClinica.aceptar(a.id); await cargarAcuerdos(); } catch (e: any) { error = e.message; }
  }
  async function rechazarEntrante(a: AcuerdoClinica) {
    try { await AcuerdosClinica.rechazar(a.id); await cargarAcuerdos(); } catch (e: any) { error = e.message; }
  }

  // Modelo B · estado de cuenta como clínica
  let estadoCuentaClinica = $state<EstadoCuentaCompartido[]>([]);
  async function cargarEstadoCuentaClinica() {
    try { estadoCuentaClinica = await Compartido.estadoCuenta(); } catch (e: any) { error = e.message; }
  }

  $effect(() => { recargar(); cargarLiquidaciones(); cargarTarifas(); cargarConvenios(); cargarCuentasCons(); cargarAcuerdos(); cargarEstadoCuentaClinica(); });
</script>

<section class="consultorios-page">
  <h2>Consultorios</h2>
  <p class="sub">Gestione unidades médicas y profesionales asociados a su clínica.</p>

  {#if error}<div class="err">{error}</div>{/if}
  {#if info}<div class="ok">{info}</div>{/if}

  <nav class="tabs">
    <button class:active={tab==='crear'} onclick={()=>tab='crear'}>Crear</button>
    <button class:active={tab==='lista'} onclick={()=>tab='lista'}>Lista</button>
    <button class:active={tab==='tarifas'} onclick={()=>tab='tarifas'}>Tarifas</button>
    <button class:active={tab==='finanzas'} onclick={()=>tab='finanzas'}>Finanzas</button>
    <button class:active={tab==='acuerdos'} onclick={()=>tab='acuerdos'}>Compartir</button>
  </nav>

  {#if tab === 'crear'}
    <div class="cols">
      <div class="card">
        <h3>Nuevo consultorio</h3>
        <div class="form">
          <input placeholder="Código" bind:value={nuevoCons.codigo}/>
          <input placeholder="Nombre" bind:value={nuevoCons.nombre}/>
          <div class="row">
            <select bind:value={nuevoCons.tipo}>
              <option value="PROPIO">Propio</option>
              <option value="ARRENDADO">Arrendado</option>
            </select>
            <select bind:value={nuevoCons.vinculo}>
              <option value="INTERNO">Interno</option>
              <option value="EXTERNO">Externo</option>
            </select>
          </div>
          {#if nuevoCons.vinculo === 'EXTERNO'}
            <input placeholder="ID del tenant externo" bind:value={nuevoCons.tenantExternoId}/>
          {/if}
          {#if nuevoCons.tipo === 'ARRENDADO'}
            <div class="row">
              <select bind:value={nuevoCons.modeloCobro}>
                <option value="COMISION">Comisión</option>
                <option value="RENTA_FIJA">Renta fija</option>
                <option value="MIXTO">Mixto</option>
              </select>
              <input type="number" min="0" max="100" step="0.01" placeholder="% comisión" bind:value={nuevoCons.comisionHospitalPct}/>
              <input type="number" min="0" step="0.01" placeholder="Renta mensual Q" bind:value={nuevoCons.rentaMensual}/>
            </div>
          {/if}
          <select bind:value={nuevoCons.medicoResponsableId}>
            <option value={undefined}>Sin médico responsable</option>
            {#each profesionales as p (p.id)}<option value={p.id}>{p.nombre}</option>{/each}
          </select>
          <button onclick={crearConsultorio}>Crear consultorio</button>
        </div>
      </div>

      <div class="card">
        <h3>Nuevo profesional</h3>
        <div class="form">
          <input placeholder="Nombre completo" bind:value={nuevoProf.nombre}/>
          <input placeholder="Colegiado" bind:value={nuevoProf.colegiado}/>
          <input placeholder="NIT" bind:value={nuevoProf.nit}/>
          <div class="row">
            <select bind:value={nuevoProf.tipoRelacion}>
              <option value="EMPLEADO">Empleado</option>
              <option value="INDEPENDIENTE">Independiente</option>
            </select>
            <input type="number" min="0" max="100" step="0.01" placeholder="% honorario" bind:value={nuevoProf.honorarioPct}/>
          </div>
          <button onclick={crearProfesional}>Crear profesional</button>
        </div>
      </div>
    </div>

  {:else if tab === 'lista'}
    <div class="card">
      <h3>Consultorios</h3>
      <table>
        <thead><tr><th>Código</th><th>Nombre</th><th>Tipo</th><th>Vínculo</th><th>Modelo</th><th>% com.</th><th>Renta</th><th>Activo</th><th></th></tr></thead>
        <tbody>
          {#each consultorios as c (c.id)}
            <tr class:off={!c.activo}>
              <td>{c.codigo}</td><td>{c.nombre}</td><td>{c.tipo}</td>
              <td>{c.vinculo === 'EXTERNO' ? 'Externo' : 'Interno'}</td>
              <td>{c.tipo === 'ARRENDADO' ? (c.modeloCobro ?? '—') : '—'}</td>
              <td>{c.tipo === 'ARRENDADO' ? `${c.comisionHospitalPct ?? 0}%` : '—'}</td>
              <td>{c.tipo === 'ARRENDADO' ? `Q${c.rentaMensual ?? 0}` : '—'}</td>
              <td>{c.activo ? 'Sí' : 'No'}</td>
              <td><button onclick={() => toggleConsultorio(c)}>{c.activo ? 'Desactivar' : 'Activar'}</button></td>
            </tr>
          {/each}
          {#if consultorios.length === 0 && !cargando}<tr><td colspan="9">No hay consultorios registrados.</td></tr>{/if}
        </tbody>
      </table>
    </div>

    <div class="card" style="margin-top:16px">
      <h3>Profesionales</h3>
      <table>
        <thead><tr><th>Nombre</th><th>Colegiado</th><th>Relación</th><th>% honorario</th><th>Activo</th><th></th></tr></thead>
        <tbody>
          {#each profesionales as p (p.id)}
            <tr class:off={!p.activo}>
              <td>{p.nombre}</td><td>{p.colegiado ?? '—'}</td><td>{p.tipoRelacion}</td>
              <td>{p.honorarioPct ?? 100}%</td><td>{p.activo ? 'Sí' : 'No'}</td>
              <td><button onclick={() => toggleProfesional(p)}>{p.activo ? 'Desactivar' : 'Activar'}</button></td>
            </tr>
          {/each}
          {#if profesionales.length === 0 && !cargando}<tr><td colspan="6">No hay profesionales registrados.</td></tr>{/if}
        </tbody>
      </table>
    </div>

  {:else if tab === 'tarifas'}
    <div class="card">
      <h3>Tarifario interno</h3>
      <p class="desc">Precios preferenciales para consultorios. Se aplican automáticamente cuando el pagador es el consultorio.</p>
      <div class="form">
        <input placeholder="Código del servicio" bind:value={nuevaTarifa.codigo}/>
        <input placeholder="Descripción" bind:value={nuevaTarifa.descripcion}/>
        <select bind:value={nuevaTarifa.centroIngreso}>
          <option value="">— Centro de ingreso —</option>
          <option value="LABORATORIO">Laboratorio</option>
          <option value="IMAGENOLOGIA">Imagenología</option>
          <option value="HONORARIO">Honorario</option>
          <option value="FARMACIA">Farmacia</option>
          <option value="OTROS">Otros</option>
        </select>
        <input type="number" min="0" step="0.01" placeholder="Precio público Q" bind:value={nuevaTarifa.precioPublico}/>
        <input type="number" min="0" step="0.01" placeholder="Precio interno Q" bind:value={nuevaTarifa.precioInterno}/>
        <button onclick={crearTarifa}>Crear tarifa</button>
      </div>
      <table>
        <thead><tr><th>Código</th><th>Descripción</th><th>Centro</th><th>Público</th><th>Interno</th><th>Activo</th><th></th></tr></thead>
        <tbody>
          {#each tarifas as t (t.id)}
            <tr class:off={!t.activo}>
              <td>{t.codigo}</td><td>{t.descripcion}</td><td>{t.centroIngreso ?? '—'}</td>
              <td>Q{t.precioPublico}</td><td>{t.precioInterno != null ? `Q${t.precioInterno}` : '—'}</td>
              <td>{t.activo ? 'Sí' : 'No'}</td>
              <td><button onclick={() => toggleTarifa(t)}>{t.activo ? 'Desactivar' : 'Activar'}</button></td>
            </tr>
          {/each}
          {#if tarifas.length === 0}<tr><td colspan="7">No hay tarifas definidas.</td></tr>{/if}
        </tbody>
      </table>
    </div>

    <div class="card" style="margin-top:16px">
      <h3>Convenios con aseguradoras</h3>
      <p class="desc">Precios pactados por servicio con cada aseguradora.</p>
      <div class="form">
        <input placeholder="Código del servicio" bind:value={nuevoConvenio.codigo}/>
        <select bind:value={nuevoConvenio.aseguradoraId}>
          <option value="">— Aseguradora —</option>
          {#each aseguradoras as a (a.id)}<option value={a.id}>{a.nombre}</option>{/each}
        </select>
        <input type="number" min="0" step="0.01" placeholder="Precio convenio Q" bind:value={nuevoConvenio.precioConvenio}/>
        <button onclick={crearConvenio}>Crear convenio</button>
      </div>
      <table>
        <thead><tr><th>Código</th><th>Aseguradora</th><th>Precio</th><th>Activo</th><th></th></tr></thead>
        <tbody>
          {#each convenios as c (c.id)}
            <tr class:off={!c.activo}>
              <td>{c.codigo}</td><td>{nombreAseguradora(c.aseguradoraId)}</td>
              <td>Q{c.precioConvenio}</td><td>{c.activo ? 'Sí' : 'No'}</td>
              <td><button onclick={() => toggleConvenio(c)}>{c.activo ? 'Desactivar' : 'Activar'}</button></td>
            </tr>
          {/each}
          {#if convenios.length === 0}<tr><td colspan="5">No hay convenios con aseguradoras.</td></tr>{/if}
        </tbody>
      </table>
    </div>

  {:else if tab === 'finanzas'}
    <div class="card">
      <h3>Reporte de ingresos</h3>
      <div class="form">
        <input type="date" bind:value={desde}/>
        <span class="sep">→</span>
        <input type="date" bind:value={hasta}/>
        <button onclick={generarReporte}>Generar</button>
      </div>
      {#if reporte}
        <div class="summary">
          <span>Total: <strong>Q{reporte.totalIngresos}</strong></span>
          <span>Comisión hospital: <strong>Q{reporte.comisionHospitalEstimada}</strong></span>
        </div>
        <table>
          <thead><tr><th>Consultorio</th><th>Renglones</th><th>Ingresos</th><th>Comisión</th><th>Centros</th></tr></thead>
          <tbody>
            {#each reporte.consultorios as r (r.consultorioId)}
              <tr>
                <td>{r.nombre}</td><td>{r.renglones}</td><td>Q{r.totalIngresos}</td>
                <td>Q{r.comisionHospitalEstimada}</td><td class="muted">{centros(r.porCentroIngreso)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>

    <div class="card" style="margin-top:16px">
      <h3>Liquidaciones</h3>
      <p class="desc">Reparto de ingresos de consultorios arrendados (usa el período de arriba).</p>
      <div class="form">
        <select bind:value={liqConsultorioId}>
          <option value="">Todos los arrendados</option>
          {#each consultorios.filter(c => c.tipo === 'ARRENDADO') as c (c.id)}<option value={c.id}>{c.nombre}</option>{/each}
        </select>
        <button onclick={generarLiquidaciones}>Generar</button>
      </div>
      <table>
        <thead><tr><th>Consultorio</th><th>Período</th><th>Ingresos</th><th>Comisión</th><th>Neto médico</th><th>Estado</th><th></th></tr></thead>
        <tbody>
          {#each liquidaciones as l (l.id)}
            <tr>
              <td>{l.consultorioNombre ?? '—'}</td><td>{l.desde} → {l.hasta}</td>
              <td>Q{l.ingresosTotales}</td><td>Q{l.comisionHospital}</td>
              <td><strong>Q{l.netoPagar ?? l.netoMedico}</strong></td><td>{l.estado}</td>
              <td>
                {#if l.estado === 'BORRADOR'}<button onclick={() => emitirLiq(l)}>Emitir</button>{/if}
                {#if l.estado === 'BORRADOR' || l.estado === 'EMITIDA'}<button onclick={() => pagarLiq(l)}>Pagar</button>{/if}
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <div class="card" style="margin-top:16px">
      <h3>Cuentas por cobrar</h3>
      <p class="desc">Saldos de servicios consumidos por cada consultorio. Los cargos son automáticos; aquí se registran abonos.</p>
      <table>
        <thead><tr><th>Consultorio</th><th>Cargos</th><th>Abonos</th><th>Saldo</th><th></th></tr></thead>
        <tbody>
          {#each cuentasCons as c (c.id)}
            <tr class:off={c.estado === 'SALDADA'}>
              <td>{c.consultorioNombre ?? '—'}</td><td>Q{c.totalCargos}</td><td>Q{c.totalAbonos}</td>
              <td><strong>Q{c.saldo}</strong></td>
              <td>{#if c.saldo > 0}<button onclick={() => abonar(c)}>Abonar</button>{/if}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    <div class="card" style="margin-top:16px">
      <h3>Estado de cuenta</h3>
      <p class="desc">Lo que cada consultorio debe al hospital en el período.</p>
      <button onclick={generarEstadoCuenta}>Generar estado de cuenta</button>
      {#if estadoCuenta}
        <div class="summary" style="margin-top:12px">
          <span>Total adeudado: <strong>Q{estadoCuenta.totalAdeudado}</strong></span>
        </div>
        <table>
          <thead><tr><th>Consultorio</th><th>Renglones</th><th>Adeudado</th><th>Centros</th></tr></thead>
          <tbody>
            {#each estadoCuenta.consultorios as r (r.consultorioId)}
              <tr><td>{r.nombre}</td><td>{r.renglones}</td><td><strong>Q{r.totalAdeudado}</strong></td><td class="muted">{centros(r.porCentroIngreso)}</td></tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </div>

  {:else if tab === 'acuerdos'}
    <div class="card">
      <h3>Acuerdos con clínicas externas</h3>
      <p class="desc">Proponga compartir resultados e información con clínicas que tienen su propio tenant.</p>
      <div class="form">
        <select bind:value={acuerdoConsultorioId}>
          <option value="">— selecciona consultorio externo —</option>
          {#each consultorios.filter(c => c.vinculo === 'EXTERNO') as c (c.id)}<option value={c.id}>{c.nombre}</option>{/each}
        </select>
        <button onclick={proponerAcuerdo}>Proponer acuerdo</button>
      </div>
      <table>
        <thead><tr><th>Clínica</th><th>Tenant</th><th>Comparte</th><th>Estado</th><th></th></tr></thead>
        <tbody>
          {#each acuerdos as a (a.id)}
            <tr>
              <td>{a.clinicaNombre ?? '—'}</td><td><code>{a.clinicaTenantId}</code></td>
              <td>{[a.comparteResultadosLab && 'Lab', a.comparteResultadosImagen && 'Imagen', a.comparteEstadoCuenta && 'Edo. cuenta'].filter(Boolean).join(', ') || '—'}</td>
              <td>{a.estado}</td>
              <td>
                {#if a.estado !== 'TERMINADO'}<button onclick={() => suspenderAcuerdo(a)}>Suspender</button>{/if}
                {#if a.estado !== 'TERMINADO'}<button onclick={() => terminarAcuerdo(a)}>Terminar</button>{/if}
              </td>
            </tr>
          {/each}
          {#if acuerdos.length === 0}<tr><td colspan="5">Sin acuerdos propuestos.</td></tr>{/if}
        </tbody>
      </table>
    </div>

    <div class="card" style="margin-top:16px">
      <h3>Acuerdos entrantes</h3>
      <p class="desc">Propuestas de hospitales para compartir información con su clínica.</p>
      <table>
        <thead><tr><th>Propuesto por</th><th>Comparte</th><th>Estado</th><th></th></tr></thead>
        <tbody>
          {#each entrantes as a (a.id)}
            <tr>
              <td><code>{a.tenantId ?? a.clinicaNombre ?? '—'}</code></td>
              <td>{[a.comparteResultadosLab && 'Lab', a.comparteResultadosImagen && 'Imagen', a.comparteEstadoCuenta && 'Edo. cuenta'].filter(Boolean).join(', ') || '—'}</td>
              <td>{a.estado}</td>
              <td>
                {#if a.estado !== 'ACEPTADO' && a.estado !== 'TERMINADO'}<button onclick={() => aceptarEntrante(a)}>Aceptar</button>{/if}
                {#if a.estado !== 'TERMINADO'}<button onclick={() => rechazarEntrante(a)}>Rechazar</button>{/if}
              </td>
            </tr>
          {/each}
          {#if entrantes.length === 0}<tr><td colspan="4">No hay acuerdos entrantes.</td></tr>{/if}
        </tbody>
      </table>
    </div>

    <div class="card" style="margin-top:16px">
      <h3>Mi estado de cuenta con hospitales</h3>
      <p class="desc">Lo que su clínica debe a hospitales aliados.</p>
      <table>
        <thead><tr><th>Hospital</th><th>Cargos</th><th>Abonos</th><th>Saldo</th><th>Estado</th></tr></thead>
        <tbody>
          {#each estadoCuentaClinica as e (e.consultorioId)}
            <tr>
              <td><code>{e.hospitalTenantId}</code></td><td>Q{e.totalCargos}</td><td>Q{e.totalAbonos}</td>
              <td><strong>Q{e.saldo}</strong></td><td>{e.estadoCuenta}</td>
            </tr>
          {/each}
          {#if estadoCuentaClinica.length === 0}<tr><td colspan="5">Sin estado de cuenta con hospitales aliados.</td></tr>{/if}
        </tbody>
      </table>
    </div>
  {/if}
</section>

<style>
  .consultorios-page { padding: 16px; max-width: 960px; }
  h2 { margin: 0 0 4px; font-size: 22px; }
  .sub { color: #64748b; font-size: 13px; margin: 0 0 20px; }
  .err { background: #fef2f2; color: #dc2626; padding: 10px 14px; border-radius: 8px; margin-bottom: 12px; font-size: 13px; }
  .ok { background: #ecfdf5; color: #0f766e; padding: 10px 14px; border-radius: 8px; margin-bottom: 12px; font-size: 13px; }

  .tabs { display: flex; gap: 0; margin-bottom: 20px; border-bottom: 2px solid rgba(0,0,0,0.05); }
  .tabs button {
    background: none; border: 0; padding: 10px 20px; font: inherit; font-size: 13px; font-weight: 600;
    color: #64748b; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px;
    transition: color 0.15s, border-color 0.15s;
  }
  .tabs button:hover { color: #0f172a; }
  .tabs button.active { color: #0f766e; border-bottom-color: #0f766e; }

  .card { background: #fff; border: 1px solid rgba(0,0,0,0.05); border-radius: 12px; padding: 24px; }
  .card h3 { font-size: 16px; margin: 0 0 12px; color: #0f172a; }
  .desc { color: #64748b; font-size: 12.5px; margin: 0 0 12px; }

  .cols { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  @media(max-width:640px){ .cols{grid-template-columns:1fr} }

  .form { display: flex; gap: 8px; flex-wrap: wrap; align-items: end; margin-bottom: 12px; }
  .form input, .form select {
    padding: 8px 12px; border: 1.5px solid rgba(0,0,0,0.08); border-radius: 8px;
    font: inherit; font-size: 13px; background: #fff; flex: 1; min-width: 120px;
  }
  .form input:focus, .form select:focus { outline: none; border-color: #0f766e; }
  .form button {
    padding: 8px 16px; background: #1e293b; color: #fff; border: 0; border-radius: 8px;
    font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;
  }
  .form button:hover { background: #0f172a; }
  .row { display: flex; gap: 8px; flex: 1; min-width: 200px; }
  .sep { color: #94a3b8; font-size: 13px; display: flex; align-items: center; }

  .summary { display: flex; gap: 20px; flex-wrap: wrap; background: #f8fafc; padding: 10px 14px; border-radius: 8px; margin: 12px 0; font-size: 13px; }

  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12.5px; }
  th { text-align: left; padding: 8px 10px; border-bottom: 2px solid rgba(0,0,0,0.05); color: #64748b; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; }
  td { padding: 8px 10px; border-bottom: 1px solid rgba(0,0,0,0.04); }
  tr.off td { color: #94a3b8; }
  td.muted { color: #94a3b8; font-size: 11px; }
  td button {
    padding: 4px 10px; background: none; border: 1px solid rgba(0,0,0,0.1); border-radius: 6px;
    font: inherit; font-size: 11px; font-weight: 600; cursor: pointer; color: #475569;
  }
  td button:hover { background: #f1f5f9; color: #0f172a; }
  code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 12px; }
</style>
